import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Icon, Input, Label } from 'semantic-ui-react'
import { PaginationComponent } from 'components'
import { createView, deleteContact, deleteView, getContacts } from 'utility/services/crm'
import {
    BUILT_IN_VIEWS,
    CONTACT_PAGE_SIZE,
    DEFAULT_SORT,
    activeFilterCount,
    filtersOf,
    nextSort,
    pageCount,
    queryForView,
    sameFilters,
} from 'utility/crm/contact-query'
import { toCsv, downloadText } from 'utility/crm/csv'
import { formatShortDate, toDateInput } from 'utility/crm/dates'
import { ALL_CRM_SCOPES, invalidateCrm, useContacts, useCrmToast, useIsCrmAdmin, usePreference, useSavedViews } from 'hooks/crm'
import { useDebounce } from 'hooks/debounce'
import { ConfirmDialog, CrmModal } from './CrmOverlay'
import { ContactBulkBar } from './ContactBulkBar'
import { ContactFilters } from './ContactFilters'
import { ContactFormModal } from './ContactFormModal'
import { ContactsTable } from './ContactsTable'
import { ContactPanel } from './ContactPanel'
import { AsyncState, ChipButton, Muted, Row } from './ui'

const EXPORT_PAGE_SIZE = 200

const EXPORT_COLUMNS = [
    { header: 'Name', value: c => c.Name },
    { header: 'Title', value: c => c.Title },
    { header: 'Company', value: c => c.AccountName },
    { header: 'Email', value: c => c.Email },
    { header: 'Phone', value: c => c.Phone },
    { header: 'Type', value: c => c.ContactType },
    { header: 'Stage', value: c => c.Stage },
    { header: 'Owner', value: c => c.OwnerName },
    { header: 'Tags', value: c => c.Tags },
    { header: 'Last contacted', value: c => (c.LastContacted ? formatShortDate(c.LastContacted) : '') },
    { header: 'Next action', value: c => c.NextTask?.Title },
    { header: 'Next action due', value: c => (c.NextTask?.DueDate ? formatShortDate(c.NextTask.DueDate) : '') },
]

/**
 * Contacts: saved view chips, filter panel, server-paged sortable table, bulk bar, export, panel.
 * @param {{ openContactId: number|null, onOpenContact: (id: number|null) => void, newContactOpen: boolean, onCloseNewContact: () => void }} props
 */
export const ContactsView = ({ openContactId, onOpenContact, newContactOpen, onCloseNewContact }) => {
    const isAdmin = useIsCrmAdmin()
    const { successToast, showError } = useCrmToast()
    const [lastQuery, setLastQuery] = usePreference('contacts.lastQuery', queryForView({}))
    // The search box is deliberately not remembered: a stale search after a reload just looks like missing data.
    const [query, setQuery] = useState(() => {
        const { Search, ...remembered } = lastQuery
        return queryForView(remembered, remembered)
    })
    const [searchText, setSearchText] = useState('')
    const debouncedSearch = useDebounce(searchText, 300)
    const [showFilters, setShowFilters] = useState(false)
    const [selected, setSelected] = useState(() => new Set())
    const [exporting, setExporting] = useState(false)
    const [editing, setEditing] = useState(null)
    const [deleting, setDeleting] = useState(null)
    const [deleteBusy, setDeleteBusy] = useState(false)
    const [saveViewOpen, setSaveViewOpen] = useState(false)
    const [viewName, setViewName] = useState('')
    const [viewBusy, setViewBusy] = useState(false)

    const contacts = useContacts(query)
    const views = useSavedViews()

    // Remember the filters + sort (not the page) per user.
    useEffect(() => {
        const { Search, ...filters } = filtersOf(query)
        setLastQuery({ ...filters, Sort: query.Sort, Dir: query.Dir })
    }, [query.Search, query.Type, query.OwnerUserId, query.Mine, query.Stage, query.Industry, query.Tag, query.Stale, query.AccountId, query.Sort, query.Dir])

    useEffect(() => {
        const value = debouncedSearch.trim()
        if ((query.Search || '') !== value) {
            setQuery(q => ({ ...q, Search: value || undefined, Page: 1 }))
        }
    }, [debouncedSearch])

    const patchQuery = useCallback(patch => setQuery(q => ({ ...q, ...patch, Page: 1 })), [])

    const applyView = filters => {
        const next = queryForView(filters, query)
        setQuery(next)
        setSearchText(next.Search || '')
        setSelected(new Set())
    }

    const clearFilters = () => {
        setQuery(q => ({ ...queryForView({}, q) }))
        setSearchText('')
    }

    const items = contacts.data?.Items || []
    const total = contacts.data?.TotalCount || 0
    const pageSize = contacts.data?.PageSize || query.PageSize || CONTACT_PAGE_SIZE
    const firstShown = total === 0 ? 0 : ((contacts.data?.Page || 1) - 1) * pageSize + 1
    const lastShown = Math.min(total, firstShown + items.length - 1)

    const toggle = id =>
        setSelected(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    const togglePage = checked =>
        setSelected(prev => {
            const next = new Set(prev)
            items.forEach(c => (checked ? next.add(c.ContactId) : next.delete(c.ContactId)))
            return next
        })

    const savedViews = views.data || []
    const filterBadge = activeFilterCount(query)

    const exportCsv = async () => {
        setExporting(true)
        try {
            const base = { ...query, PageSize: EXPORT_PAGE_SIZE }
            const first = await getContacts({ ...base, Page: 1 })
            let rows = first.Items || []
            const pages = pageCount(first.TotalCount, EXPORT_PAGE_SIZE)
            for (let page = 2; page <= pages; page += 1) {
                // Sequential on purpose: keeps the load on the API low and the order stable.
                // eslint-disable-next-line no-await-in-loop
                const next = await getContacts({ ...base, Page: page })
                rows = rows.concat(next.Items || [])
            }
            downloadText(`crm-contacts-${toDateInput(new Date())}.csv`, toCsv(rows, EXPORT_COLUMNS))
            successToast(`Exported ${rows.length} contact${rows.length === 1 ? '' : 's'}`)
        } catch (err) {
            showError(err)
        } finally {
            setExporting(false)
        }
    }

    const saveView = async () => {
        const name = viewName.trim()
        if (!name) return
        setViewBusy(true)
        try {
            await createView({ Name: name, Filters: { ...filtersOf(query), Sort: query.Sort, Dir: query.Dir } })
            successToast('View saved')
            invalidateCrm('views')
            setSaveViewOpen(false)
            setViewName('')
        } catch (err) {
            showError(err)
        } finally {
            setViewBusy(false)
        }
    }

    const removeView = async view => {
        try {
            await deleteView(view.ViewId)
            successToast('View deleted')
            invalidateCrm('views')
        } catch (err) {
            showError(err)
        }
    }

    const confirmDelete = async () => {
        setDeleteBusy(true)
        try {
            await deleteContact(deleting.ContactId)
            successToast('Contact deleted')
            if (openContactId === deleting.ContactId) onOpenContact(null)
            invalidateCrm(...ALL_CRM_SCOPES)
            setDeleting(null)
        } catch (err) {
            showError(err)
            setDeleting(null)
        } finally {
            setDeleteBusy(false)
        }
    }

    const empty = useMemo(() => Boolean(contacts.data) && items.length === 0, [contacts.data, items.length])
    const filtered = filterBadge > 0 || Boolean(query.Search)

    return (
        <div>
            <Row $between $wrap style={{ marginBottom: 10 }}>
                <h3 style={{ margin: 0 }}>Contacts</h3>
                <Row $gap={8} $wrap>
                    <Input
                        icon="search"
                        iconPosition="left"
                        placeholder="Filter this list…"
                        value={searchText}
                        onChange={(_, d) => setSearchText(d.value)}
                        aria-label="Filter this list"
                    />
                    <Button type="button" basic onClick={() => setShowFilters(s => !s)} aria-expanded={showFilters}>
                        <Icon name="filter" />
                        Filters
                        {filterBadge ? (
                            <Label circular size="mini" color="blue" style={{ marginLeft: 6 }}>
                                {filterBadge}
                            </Label>
                        ) : null}
                    </Button>
                    <Button type="button" basic onClick={exportCsv} loading={exporting} disabled={exporting || total === 0}>
                        <Icon name="download" />
                        Export
                    </Button>
                </Row>
            </Row>

            <Row $gap={6} $wrap style={{ marginBottom: 12 }} role="group" aria-label="Saved views">
                {BUILT_IN_VIEWS.map(view => (
                    <ChipButton key={view.key} type="button" $active={sameFilters(query, view.filters)} onClick={() => applyView(view.filters)}>
                        {view.name}
                    </ChipButton>
                ))}
                {savedViews.map(view => (
                    <span key={view.ViewId} style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <ChipButton type="button" $active={sameFilters(query, view.Filters)} onClick={() => applyView(view.Filters)}>
                            {view.Name}
                        </ChipButton>
                        <button
                            type="button"
                            aria-label={`Delete saved view ${view.Name}`}
                            onClick={() => removeView(view)}
                            style={{ background: 'none', border: 0, cursor: 'pointer', color: 'inherit', opacity: 0.6 }}
                        >
                            ×
                        </button>
                    </span>
                ))}
                {filtered ? (
                    <ChipButton type="button" onClick={() => setSaveViewOpen(true)}>
                        + Save view
                    </ChipButton>
                ) : null}
                {views.error ? <Muted role="alert">Saved views unavailable: {views.error.message}</Muted> : null}
            </Row>

            {showFilters ? <ContactFilters query={query} onChange={patchQuery} onClear={clearFilters} /> : null}

            <ContactBulkBar selectedIds={Array.from(selected)} onClear={() => setSelected(new Set())} />

            <AsyncState
                loading={contacts.loading}
                error={contacts.error}
                hasData={Boolean(contacts.data)}
                empty={empty}
                emptyText={filtered || query.Search ? 'No contacts match these filters.' : 'No contacts yet. Add one or import a CSV.'}
                onRetry={contacts.refetch}
            >
                <div aria-busy={contacts.loading} style={{ opacity: contacts.loading ? 0.6 : 1 }}>
                    <ContactsTable
                        items={items}
                        query={{ ...DEFAULT_SORT, ...query }}
                        selected={selected}
                        isAdmin={isAdmin}
                        onSort={column => setQuery(q => nextSort(q, column))}
                        onToggle={toggle}
                        onTogglePage={togglePage}
                        onOpen={c => onOpenContact(c.ContactId)}
                        onEdit={setEditing}
                        onDelete={setDeleting}
                    />
                </div>
                <Row $between $wrap style={{ marginTop: 12 }}>
                    <Muted role="status">
                        Showing {firstShown}–{lastShown} of {total} contacts
                    </Muted>
                    {total > pageSize ? (
                        <PaginationComponent
                            activePage={contacts.data?.Page || query.Page || 1}
                            totalCount={total}
                            pageSize={pageSize}
                            pageChanged={page => setQuery(q => ({ ...q, Page: page }))}
                        />
                    ) : null}
                </Row>
            </AsyncState>

            <ContactPanel contactId={openContactId} onClose={() => onOpenContact(null)} onSwitch={onOpenContact} />

            <ContactFormModal
                open={newContactOpen || Boolean(editing)}
                contact={editing}
                onClose={() => {
                    setEditing(null)
                    onCloseNewContact()
                }}
                onSaved={() => {}}
            />

            <ConfirmDialog
                open={Boolean(deleting)}
                danger
                title="Delete contact"
                message={deleting ? `Delete ${deleting.Name}? This also removes their follow-ups.` : ''}
                confirmLabel="Delete"
                loading={deleteBusy}
                onConfirm={confirmDelete}
                onClose={() => setDeleting(null)}
            />

            <CrmModal
                open={saveViewOpen}
                onClose={() => setSaveViewOpen(false)}
                title="Save this view"
                width={420}
                footer={
                    <>
                        <Button type="button" onClick={() => setSaveViewOpen(false)} disabled={viewBusy}>
                            Cancel
                        </Button>
                        <Button type="button" primary onClick={saveView} loading={viewBusy} disabled={viewBusy || !viewName.trim()}>
                            Save view
                        </Button>
                    </>
                }
            >
                <Input
                    fluid
                    placeholder="View name"
                    value={viewName}
                    maxLength={60}
                    onChange={(_, d) => setViewName(d.value)}
                    onKeyDown={event => {
                        if (event.key === 'Enter') saveView()
                    }}
                    aria-label="View name"
                />
            </CrmModal>
        </div>
    )
}
